"use client";

import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { MessageSquare } from "lucide-react";
import { SilaeMemoEditor } from "./SilaeMemoEditor";
import type { FormikProps } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { useTranslation } from "react-i18next";

interface MemoSectionProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix?: string; // Nouveau système: chemin complet (ex: "ifBranch.bonuses.0")
  title?: string;
}

/**
 * Section mémo réutilisable pour les étapes de configuration des primes
 */
export const MemoSection: React.FC<MemoSectionProps> = ({
  formik,
  fieldPrefix,
  title,
}) => {
  const { t } = useTranslation();

  // Helper pour accéder aux valeurs imbriquées
  const getFieldValue = (path: string) => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = formik.values;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return undefined;
    }
    return value;
  };

  const enableField = fieldPrefix
    ? `${fieldPrefix}.enableMemos`
    : "enableMemos";

  const memoField = fieldPrefix ? `${fieldPrefix}.memoLines` : "memoLines";

  const isEnabled = getFieldValue(enableField) as boolean;

  const sectionTitle = title || t("memoSection.title");

  return (
    <Card className="bg-background dark:bg-background">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            <div className="flex flex-col ">
              <CardTitle className="text-lg">
                {sectionTitle}
                <Badge variant="outline" className="text-xs ml-2">
                  {t("memoSection.optional")}
                </Badge>
              </CardTitle>
              <CardDescription>{t("memoSection.description")}</CardDescription>
            </div>
          </div>
          <Switch
            id="enable-memos"
            checked={isEnabled}
            onCheckedChange={(checked) => {
              formik.setFieldValue(enableField, checked);
              setTimeout(() => formik.validateForm(), 0);
            }}
          />
        </div>
      </CardHeader>
      {isEnabled && (
        <CardContent className="space-y-4">
          {/* Éditeur de mémos */}
          <div className="border-t pt-4">
            <SilaeMemoEditor
              fieldPrefix={fieldPrefix}
              formik={{
                ...formik,
                values: {
                  ...formik.values,
                  memoLines: getFieldValue(memoField) as string,
                } as SilaeFormData,
                setFieldValue: (
                  field: string,
                  value: unknown,
                  shouldValidate?: boolean,
                ) => {
                  if (field === "memoLines") {
                    return formik.setFieldValue(
                      memoField,
                      value,
                      shouldValidate,
                    );
                  }
                  return formik.setFieldValue(field, value, shouldValidate);
                },
              }}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};
